/**
 * AWARE Health — ProvinceList Component
 * Scrollable list of provinces with inline risk badges, sorted by severity.
 * Adapts to view mode: simple view hides numeric rate.
 * Theme-aware and translated.
 */

import { useTranslation } from 'react-i18next'
import RiskBadge from './RiskBadge'
import { useTheme } from '../../contexts/ThemeContext'
import { useViewMode } from '../../contexts/ViewModeContext'
import type { ForecastRecord, AlertLevel, RiskCategory } from '../../types/forecast'

interface ProvinceListProps {
  records: ForecastRecord[]
  selectedRegionId: string | null
  hoveredRegionId?: string | null
  onSelect: (regionId: string) => void
  onHover?: (regionId: string | null) => void
}

const ALERT_ORDER: Record<AlertLevel, number> = {
  Emergency: 0,
  Warning:   1,
  Watch:     2,
  None:      3,
}

const RISK_ORDER: Record<RiskCategory, number> = {
  high:   0,
  medium: 1,
  low:    2,
}

export default function ProvinceList({
  records,
  selectedRegionId,
  hoveredRegionId,
  onSelect,
  onHover,
}: ProvinceListProps) {
  const { theme } = useTheme()
  const { isDetailed } = useViewMode()
  const { t } = useTranslation()
  const isDark = theme === 'dark'

  const regionSummaries = (() => {
    const map = new Map<string, ForecastRecord>()
    for (const r of records) {
      const existing = map.get(r.region_id)
      if (!existing || r.horizon < existing.horizon) {
        map.set(r.region_id, r)
      }
    }
    return [...map.values()].sort((a, b) => {
      const alertDiff = (ALERT_ORDER[a.alert_level] ?? 3) - (ALERT_ORDER[b.alert_level] ?? 3)
      if (alertDiff !== 0) return alertDiff
      const riskDiff = (RISK_ORDER[a.risk_category] ?? 2) - (RISK_ORDER[b.risk_category] ?? 2)
      if (riskDiff !== 0) return riskDiff
      return a.region_name.localeCompare(b.region_name)
    })
  })()

  if (regionSummaries.length === 0) {
    return (
      <div
        style={{
          padding: '24px 16px',
          textAlign: 'center',
          color: isDark ? '#334155' : '#94a3b8',
          fontSize: 12,
        }}
      >
        {t('sidebar.noProvinceData')}
      </div>
    )
  }

  const RISK_DOTS: Record<RiskCategory, string> = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 4px' }}>
      {regionSummaries.map((r) => {
        const isActive = selectedRegionId === r.region_id
        const isHovered = hoveredRegionId === r.region_id

        return (
          <button
            key={r.region_id}
            id={`province-${r.region_id}`}
            onClick={() => onSelect(r.region_id)}
            onMouseEnter={() => onHover?.(r.region_id)}
            onMouseLeave={() => onHover?.(null)}
            className={`province-item glass-card rounded-lg w-full text-left ${isActive ? 'active' : ''}`}
            style={{
              padding: '8px 10px',
              cursor: 'pointer',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: isActive
                ? (isDark ? 'rgba(20,184,166,0.15)' : 'rgba(13,148,136,0.1)')
                : isHovered
                ? (isDark ? 'rgba(20,184,166,0.08)' : 'rgba(13,148,136,0.05)')
                : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
              borderColor: isActive
                ? (isDark ? 'rgba(20,184,166,0.6)' : 'rgba(13,148,136,0.4)')
                : isHovered
                ? (isDark ? 'rgba(20,184,166,0.35)' : 'rgba(13,148,136,0.2)')
                : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
              transform: isHovered ? 'translateX(2px)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            {/* Simple view: colored risk dot */}
            {!isDetailed && (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: RISK_DOTS[r.risk_category] ?? '#64748b',
                  flexShrink: 0,
                }}
              />
            )}

            {/* Province name */}
            <span
              style={{
                flex: 1,
                fontSize: 12,
                fontWeight: isActive || isHovered ? 600 : 400,
                color: isActive
                  ? (isDark ? '#5eead4' : '#0d9488')
                  : isHovered
                  ? (isDark ? '#cbd5e1' : '#334155')
                  : (isDark ? '#94a3b8' : '#64748b'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {r.region_name}
            </span>

            {/* Detailed view: calibrated median value */}
            {isDetailed && (
              <span
                style={{
                  fontSize: 11,
                  color: isActive ? (isDark ? '#5eead4' : '#0d9488') : (isDark ? '#475569' : '#94a3b8'),
                  fontFamily: 'JetBrains Mono, monospace',
                  flexShrink: 0,
                }}
              >
                {r.forecast_calibrated_median.toFixed(3)}
              </span>
            )}

            {/* Risk badge — compact */}
            <RiskBadge risk={r.risk_category} size="sm" />
          </button>
        )
      })}
    </div>
  )
}
